<template>
  <div>
    <div class="content-sub-header">
      <SubHeaderItem
        v-if="section === 'inscriptions'"
        class="brouillons"
        :icon="'exclamation-triangle'"
        :type="'is-danger'"
      >
        <router-link
          :to="{
            path: `/grae/details/0/${grae.id}`,
            query: {
              filters: brouillonFilters,
            },
          }"
          class="item-text"
        >
          {{ $t("instructionPage.projets-en-brouillon", [nbBrouillons]) }}
        </router-link>
      </SubHeaderItem>
      <SubHeaderItem v-if="section === 'labellisations'" class="brouillons">
        {{ $t("instructionPage.projets-en-labellisation", [nbLabellisations]) }}
      </SubHeaderItem>
      <SubHeaderItem v-if="section === 'inscriptions'" class="dates" :icon="'calendar'">
        <div class="item-text">{{ $t("myProjects.dateInscription") }}</div>
        <div class="item-text range">
          {{
            $t("myProjects.dateInscriptionRange", [
              formatDate(parametrage.debutInscription),
              formatDate(parametrage.finInscription),
            ])
          }}
        </div>
      </SubHeaderItem>
      <SubHeaderItem v-if="section === 'labellisations'" class="dates" :icon="'calendar'">
        <div class="item-text">{{ $t("myProjects.dateLabellisation") }}</div>
        <div class="item-text range">
          {{
            $t("myProjects.dateInscriptionRange", [
              formatDate(parametrage.debutLabellisation),
              formatDate(parametrage.finLabellisation),
            ])
          }}
        </div>
      </SubHeaderItem>
    </div>
    <PageView
      :main-title="title"
      :sub-title="subTitle"
      :sub-title-icon="subTitleIcon"
      :display-sub-title-in-green="true"
    >
      <ContentView>
        <div v-if="section === 'inscriptions'">
          <InscriptionsView
            v-if="grae.id"
            :grae-id="grae.id"
            :active-quick-filter="activeQuickFilter"
          ></InscriptionsView>
        </div>
        <div v-else-if="section === 'labellisations'">
          <LabellisationsView
            v-if="grae.id"
            :grae-id="grae.id"
            :active-sub-tab="activeSubTab"
            :active-quick-filter="activeQuickFilter"
          ></LabellisationsView>
        </div>
      </ContentView>
    </PageView>
  </div>
</template>

<script lang="ts">
import {
  GraeDTO,
  PaginationParameter,
  ParametrageDTO,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { Filters } from "@/model/bean/Filters"
import { InstructionService } from "@/services/rest/instruction/InstructionService"
import { LoginService } from "@/services/rest/login/LoginService"
import { OrganismeService } from "@/services/rest/organisme/OrganismeService"
import { ParametersService } from "@/services/rest/parameters/ParametersService"
import { UsersService } from "@/services/rest/users/UsersService"
import ContentView from "@/views/commons/ContentView.vue"
import PageView from "@/views/commons/PageView.vue"
import SubHeaderItem from "@/views/commons/SubHeaderItem.vue"
import { BackToPreviousScreenItem, ContextMenu, NavbarItem } from "@/views/menus/MenuItems"

import { defineComponent } from "vue"
import InscriptionsView from "./InscriptionsView.vue"
import LabellisationsView from "./labellisation/LabellisationsView.vue"
import { eventBus } from "@/services/EventBus"
import DateUtils from "@/services/DateUtils"

export default defineComponent({
  name: "InstructionPage",
  components: {
    PageView,
    ContentView,
    SubHeaderItem,
    InscriptionsView,
    LabellisationsView,
  },
  props: {
    graeId: {
      type: String,
      default: "",
      required: false,
    },
    section: {
      type: String,
      default: "inscriptions",
    },
    activeSubTab: {
      type: Number,
      default: 0,
    },
    activeQuickFilter: {
      type: Number,
      default: 0,
    },
  },

  emits: ["contextual-menu-changed"],
  data() {
    return {
      loginService: LoginService.INSTANCE,
      usersService: UsersService.INSTANCE,
      organismeService: OrganismeService.INSTANCE,
      parametersService: ParametersService.INSTANCE,
      instructionService: InstructionService.INSTANCE,

      user: new UtilisateurDTO(),
      grae: new GraeDTO(),
      parametrage: new ParametrageDTO(),

      title: "",
      subTitle: "",
      subTitleIcon: "",

      nbBrouillons: 0,
      brouillonFilters: JSON.stringify([
        { property: "typeDossier", values: ["DOSSIER_INSCRIPTION"] },
        { property: "statut", values: ["BROUILLON"] },
      ]),
      nbLabellisations: 0,
    }
  },
  watch: {
    grae(): void {
      this.reloadCount()
    },
    section(): void {
      this.activeSectionChanged()
      this.reloadCount()
    },
  },
  created(): void {
    const loggedUser = this.loginService.getLoggedUser()
    if (!loggedUser || !loggedUser.id) {
      return
    }

    if (!this.graeId) {
      this.getGraeFromLoggedUser(loggedUser.id)
    } else {
      this.getGraeFromId(loggedUser.id, this.graeId)
    }

    this.subTitle = this.$t("instructionPage.instruction").toString()
    this.activeSectionChanged()
    this.loadParameters()
    this.reloadCount()
  },

  methods: {
    reloadCount(): void {
      if (this.grae.id && this.section === "inscriptions") {
        this.countNbBrouillons()
      }
      if (this.grae.id && this.section === "labellisations") {
        this.countNbLabellisations()
      }
    },

    async countNbBrouillons(): Promise<void> {
      const page = await this.instructionService.getInscriptions(
        this.grae.id,
        this.paginationParameter(),
        Filters.asFilters([{ property: "statut", values: ["BROUILLON"] }]),
        undefined,
      )
      this.nbBrouillons = page.count
    },

    async countNbLabellisations(): Promise<void> {
      const page = await this.instructionService.getLabellisations(
        this.grae.id,
        this.paginationParameter(),
        Filters.asFilters([
          {
            property: "statut",
            values: ["EN_ATTENTE", "INSTRUCTION_EN_COURS", "INSTRUCTION_TERMINEE"],
          },
        ]),
        undefined,
      )
      this.nbLabellisations = page.count
    },

    paginationParameter(): PaginationParameter {
      const paginationParameter = new PaginationParameter()
      paginationParameter.pageNumber = 1
      paginationParameter.pageSize = 1
      return paginationParameter
    },

    async getGraeFromLoggedUser(id: string): Promise<void> {
      this.user = await this.usersService.getUserProfile(id)
      if (this.user && this.user.grae) {
        this.grae = this.user.grae as GraeDTO
      }
      this.refreshContextMenu(false)
      this.subTitle = this.$t("instructionPage.instruction").toString()
    },

    async getGraeFromId(loggedUserId: string, graedId: string): Promise<void> {
      this.user = await this.usersService.getUserProfile(loggedUserId)
      this.grae = await this.organismeService.getGrae(graedId)
      const isFromGrae = this.user.grae?.id == this.grae.id
      this.refreshContextMenu(!isFromGrae)
      this.subTitle = isFromGrae
        ? this.$t("instructionPage.instruction").toString()
        : this.grae.region
    },

    refreshContextMenu(showBackToListMenuEntry: boolean): void {
      const menuItems = []

      if (this.grae) {
        const graeId = this.grae.id
        // Back to previous screen (if comming from list)
        if (showBackToListMenuEntry) {
          menuItems.push(new BackToPreviousScreenItem())
        }

        // Sub-sections
        menuItems.push(
          new NavbarItem(
            `/instruction/inscriptions/0/0/${graeId}`,
            this.$t("instructionPage.inscriptions.title").toString(),
            "calendar",
            false,
          ),
        )
        menuItems.push(
          new NavbarItem(
            `/instruction/labellisations/0/0/${graeId}`,
            this.$t("instructionPage.labellisations.title").toString(),
            "address-card",
            false,
          ),
        )
        const possibleMenuParents = ["/instruction/inscriptions/"]
        eventBus.emit("contextual-menu-changed", new ContextMenu(possibleMenuParents, menuItems))
      }
    },

    activeSectionChanged(): void {
      this.title = this.$t(`instructionPage.${this.section}.title`).toString()
      this.subTitleIcon = ""
    },

    async loadParameters(): Promise<void> {
      this.parametrage = await this.parametersService.getParametrage(this.graeId)
    },

    formatDate(date: Date): string {
      return DateUtils.formatDateFromPattern(date, "d MMMM yyyy")
    },
  },
})
</script>

<style lang="scss">
.brouillons > div:first-of-type {
  right: 380px;
}

.dates > div:first-of-type {
  right: 15px;
}

.item-text {
  text-align: center;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;

  &.range {
    font-weight: 700;
  }
}
</style>
